import {
	createFileRoute,
	Outlet,
	useMatch,
	useParams,
} from "@tanstack/react-router";
import { Plus } from "@untitledui/icons";
import { useState } from "react";
import { z } from "zod";
import { PageHeader } from "@/components/Organisms/PageHeader/PageHeader";
import { PageLayout } from "@/components/Organisms/PageLayout/PageLayout";
import { OrgRecordsTable } from "@/features/records/components";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useCreatableSubOrganizations } from "@/hooks/use-creatable-sub-organizations";

/**
 * Org Records landing shell — table mounts in the body;
 * workspace is a full second-level page (skip landing chrome).
 */
export const Route = createFileRoute(
	"/_authenticated/(web-app)/_web/web/$orgId/records",
)({
	component: RecordsPage,
	validateSearch: (search) =>
		z
			.object({
				q: z.string().optional(),
			})
			.parse(search),
});

function RecordsPage() {
	const { t } = useAppTranslation();
	const params = useParams({
		from: "/_authenticated/(web-app)/_web/web/$orgId/records",
	});
	const workspaceMatch = useMatch({
		from: "/_authenticated/(web-app)/_web/web/$orgId/records/(record)/$recordId/workspace",
		shouldThrow: false,
	});
	const [isCreateOpen, setIsCreateOpen] = useState(false);
	const [tableOptionsHost, setTableOptionsHost] =
		useState<HTMLDivElement | null>(null);

	const canCreateRecord = useCreatableSubOrganizations({
		actionName: "CreateRecord",
		organizationId: params.orgId,
		entityType: "Record",
	});

	// Workspace is a full second-level page — skip landing table chrome.
	if (workspaceMatch) {
		return <Outlet />;
	}

	return (
		<>
			<PageLayout
				header={
					<PageHeader
						type="landing"
						title={t("main.nav.items.records.label")}
						description={t("main.records.table.description")}
						actions={
							canCreateRecord.canCreate
								? [
										{
											label: t("common.actions.add"),
											icon: Plus,
											color: "accent",
											onPress: () => {
												setIsCreateOpen(true);
											},
										},
									]
								: undefined
						}
						overflowControl={<div ref={setTableOptionsHost} />}
						overflowAriaLabel={t("common.tableActions.options")}
					/>
				}
			>
				<OrgRecordsTable
					orgId={params.orgId}
					tableOptionsPortalTarget={tableOptionsHost}
					isCreateOpen={isCreateOpen}
					onCreateOpenChange={setIsCreateOpen}
					canCreateRecord={Boolean(canCreateRecord.canCreate)}
				/>
			</PageLayout>
			<Outlet />
		</>
	);
}
